// wasmValidator.ts

/**
 * Basic WASM validation and inspection utility
 */
export class WasmValidator {
    /**
     * Validates a WebAssembly module
     */
    static async validateWasm(wasmBytes: Uint8Array): Promise<{ isValid: boolean; details: string }> {
        try {
            // Check for WASM magic number (0x0061736d)
            if (wasmBytes.length < 4 ||
                wasmBytes[0] !== 0x00 ||
                wasmBytes[1] !== 0x61 ||
                wasmBytes[2] !== 0x73 ||
                wasmBytes[3] !== 0x6d) {
                return {
                    isValid: false,
                    details: 'Invalid WASM magic number - not a valid WASM binary'
                };
            }

            // Check WASM version (should be 1)
            if (wasmBytes.length < 8 ||
                wasmBytes[4] !== 0x01 ||
                wasmBytes[5] !== 0x00 ||
                wasmBytes[6] !== 0x00 ||
                wasmBytes[7] !== 0x00) {
                return {
                    isValid: false,
                    details: 'Invalid WASM version - expected version 1'
                };
            }

            // Try to compile the WASM module
            const module = await WebAssembly.compile(wasmBytes);

            // Get module info
            const exports = WebAssembly.Module.exports(module);
            const imports = WebAssembly.Module.imports(module);

            return {
                isValid: true,
                details: `Valid WASM module:
                - Size: ${wasmBytes.length} bytes
                - Exports: ${exports.length} functions
                - Imports: ${imports.length} functions
                - Export names: ${exports.map(e => e.name).join(', ')}
                `
            };

        } catch (err: unknown) {
            // Properly handle unknown error type
            const errorMessage = err instanceof Error
                ? err.message
                : typeof err === 'string'
                    ? err
                    : 'Unknown error occurred';

            return {
                isValid: false,
                details: `WASM validation failed: ${errorMessage}`
            };
        }
    }

    /**
     * Prints a hex dump of the first few bytes for inspection
     */
    static inspectWasmHeader(wasmBytes: Uint8Array): string {
        const headerSize = Math.min(32, wasmBytes.length);
        let hexDump = 'WASM Header Hex Dump:\n';

        for (let i = 0; i < headerSize; i++) {
            hexDump += wasmBytes[i].toString(16).padStart(2, '0') + ' ';
            if ((i + 1) % 16 === 0) hexDump += '\n';
        }

        return hexDump;
    }
}

// Example usage in CompilerService:
async function validateCompiledWasm(wasmBytes: Uint8Array) {
    try {
        const validation = await WasmValidator.validateWasm(wasmBytes);
        console.log('[CompilerService] WASM validation result:', validation);

        if (!validation.isValid) {
            console.error('[CompilerService] Invalid WASM output');
            console.log(WasmValidator.inspectWasmHeader(wasmBytes));
            throw new Error(`Invalid WASM output: ${validation.details}`);
        }

        return validation;
    } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        throw new Error(`WASM validation failed: ${errorMessage}`);
    }
}